import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { boardAPI } from '../services/api';
import { BoardPost, BoardPermissions } from '../types/board';
import { usePageTitle } from '../hooks/usePageTitle';
import BoardPostCard from '../components/board/BoardPostCard';
import BoardPostForm from '../components/board/BoardPostForm';
import DeleteConfirmModal from '../components/ui/DeleteConfirmModal';

interface BoardProps {
  /** A családi nézetben a fül neve "Hírek", a lap is így hívja magát. */
  title?: string;
  subtitle?: string | null;
}

const Board: React.FC<BoardProps> = ({ title = 'Üzenőfal', subtitle = 'Gyors infók korosztályoknak és a klubnak.' }) => {
  usePageTitle('Üzenőfal');

  // Az értesítés a /board/{id} címre visz: oda görgetünk, és ha a bejegyzés
  // közben lejárt, az archívumot is bekapcsoljuk, hogy megtaláljuk.
  const { id } = useParams<{ id: string }>();
  const targetId = id ? Number(id) : null;
  const scrolledToTarget = useRef(false);

  const [posts, setPosts] = useState<BoardPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchive, setShowArchive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<BoardPost | null>(null);
  const [postToDelete, setPostToDelete] = useState<BoardPost | null>(null);

  // A klub beállítása és a saját szerepek döntik el, ki mit tehet a falon
  const [permissions, setPermissions] = useState<BoardPermissions | null>(null);
  const canPost = permissions?.canPost ?? false;

  useEffect(() => {
    boardAPI
      .getPermissions()
      .then(setPermissions)
      .catch((error) => console.error('Az üzenőfal jogosultságok betöltése nem sikerült:', error));
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await boardAPI.getPosts({ includeExpired: showArchive });
      setPosts(data);

      // Amit a felhasználó megkapott, azt olvasottnak vesszük - a badge így
      // arról szól, hogy van-e olyan bejegyzés, amit még nem kapott meg.
      const unread = data.filter((p) => !p.isReadByMe).map((p) => p.id);
      if (unread.length > 0) {
        await boardAPI.markRead(unread);
        window.dispatchEvent(new Event('board-read'));
      }
    } catch (error) {
      console.error('Az üzenőfal betöltése nem sikerült:', error);
    } finally {
      setLoading(false);
    }
  }, [showArchive]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (targetId == null || loading || scrolledToTarget.current) return;

    const element = document.getElementById(`board-post-${targetId}`);
    if (element) {
      scrolledToTarget.current = true;
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Nincs a falon: valószínűleg lejárt, próbáljuk az archívummal
    if (!showArchive) {
      setShowArchive(true);
    }
  }, [targetId, loading, posts, showArchive]);

  const handleCreate = () => {
    setEditingPost(null);
    setFormOpen(true);
  };

  const handleEdit = (post: BoardPost) => {
    setEditingPost(post);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!postToDelete) return;

    try {
      await boardAPI.delete(postToDelete.id);
      setPostToDelete(null);
      await load();
    } catch (error) {
      console.error('A bejegyzés törlése nem sikerült:', error);
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        </div>

        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showArchive}
              onChange={(e) => setShowArchive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Lejártak is
          </label>

          {canPost && (
            <button
              onClick={handleCreate}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <PlusIcon className="mr-2 h-5 w-5" />
              Új bejegyzés
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center text-gray-500">Betöltés...</div>
      ) : posts.length === 0 ? (
        <div className="rounded-lg bg-white py-12 text-center shadow">
          <p className="text-gray-500">
            {showArchive ? 'Nincs bejegyzés.' : 'Még nincs kiírva semmi az üzenőfalra.'}
          </p>
          {canPost && (
            <button
              onClick={handleCreate}
              className="mt-4 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
            >
              <PlusIcon className="mr-2 h-5 w-5" />
              Első bejegyzés
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <BoardPostCard
              key={post.id}
              highlighted={post.id === targetId}
              post={post}
              onEdit={handleEdit}
              onDelete={setPostToDelete}
              onChanged={load}
            />
          ))}
        </div>
      )}

      <BoardPostForm
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        post={editingPost}
        permissions={permissions}
        onSaved={load}
      />

      <DeleteConfirmModal
        isOpen={postToDelete !== null}
        onClose={() => setPostToDelete(null)}
        onConfirm={handleDelete}
        title="Bejegyzés törlése"
        message="Biztosan törlöd ezt a bejegyzést? A hozzászólások is törlődnek."
        itemName={postToDelete?.content.slice(0, 60) ?? ''}
      />
    </div>
  );
};

export default Board;
